import { applySnapshot, flow, types, getSnapshot } from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

import { getNextPublishStatus } from "utils/publish";

import {
  ActiveProgram,
  PUBLISH_STATUSES,
  QuickLinkModel,
} from "constants/models";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { isEmptyObject } from "utils";

export const QuickLinkDetailsModel = types
  .model({
    id: types.number,
    title: types.string,
    url: types.maybe(types.string),
    description: types.maybe(types.string),
    imageUrl: types.maybe(types.string),
    activePrograms: types.array(ActiveProgram),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
        file: self.imageUrl,
      };
    },
  }));

const ContentResourcesQuickLinksStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/quick-link/list`, params);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: QuickLinkModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(QuickLinkDetailsModel),
    isMoreLoading: types.optional(types.boolean, false),
    isQuickLinkLoading: types.optional(types.boolean, false),
    isQuickLinkManaging: types.optional(types.boolean, false),
    isQuickLinkPublishing: types.optional(types.boolean, false),
    isQuickLinkCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get quickLinkOptions() {
      const { items } = getSnapshot(self);
      return items.map(({ id, title, imageUrl, publishStatus }) => ({
        label: title,
        value: {
          id,
          imageUrl,
          title,
          publishStatus,
        },
      }));
    },
  }))
  .actions(self => ({
    updateQuickLink(id: number, data: any) {
      const { items } = getSnapshot(self);

      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchMoreQuickLinks: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          const { orderBy, orderByDesc } = self.orderContext || {};

          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/quick-link/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
            search: self.search,
            orderBy,
            orderByDesc,
            ...self.filters,
          });

          applySnapshot(self, {
            ...self,
            items: self.items.concat(items),
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    fetchQuickLinkDetails: flow(function*(id?: number) {
      self.isQuickLinkLoading = true;

      try {
        if (id) {
          const details = yield api.get(`/quick-link/${id}`);

          applySnapshot(self, {
            ...self,
            details,
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuickLinkLoading = false;
      }
    }),

    createQuickLink: flow(function*(payload: any) {
      self.isQuickLinkManaging = true;

      try {
        const { id } = yield api.postFormData(`/quick-link`, payload);
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuickLinkManaging = false;
      }
    }),

    editQuickLinkInfo: flow(function*(payload: any) {
      self.isQuickLinkManaging = true;

      try {
        return yield api.put(`/quick-link/${payload.id}`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuickLinkManaging = false;
      }
    }),

    editQuickLinkImage: flow(function*(payload: any) {
      self.isQuickLinkManaging = true;

      try {
        return yield api.putFormData(
          `/quick-link/${payload.id}/photo/upload`,
          payload,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuickLinkManaging = false;
      }
    }),

    updatePublishStatus: flow(function*(id: number, status: string) {
      self.isQuickLinkPublishing = true;

      try {
        const newStatus = getNextPublishStatus(status);
        if (newStatus) {
          const { success } = yield api.post(`/quick-link/${id}/status`, {
            status: newStatus,
          });
          if (success) {
            self.updateQuickLink(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuickLinkPublishing = false;
      }
    }),

    archiveQuickLink: flow(function*(id: number) {
      self.isQuickLinkManaging = true;

      try {
        return yield api.post(`/quick-link/${id}/archive`, {});
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuickLinkManaging = false;
      }
    }),

    copyQuickLink: flow(function*(id: number) {
      self.isQuickLinkCopying = true;

      try {
        yield api.post(`/quick-link/${id}/copy`, {});
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuickLinkCopying = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export const ContentStudioArchivedQuickLinksStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/quick-link/archived/list`, params);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: QuickLinkModel,
  searchBy: "search",
})
  .props({
    isManaging: types.optional(types.boolean, false),
    isMoreLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get options() {
      const { items } = getSnapshot(self);
      return items.map(({ id, title, imageUrl, publishStatus }) => ({
        label: title,
        value: {
          id,
          imageUrl,
          title,
          publishStatus,
        },
      }));
    },
  }))
  .actions(self => ({
    fetchMore: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/quick-link/archived/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
          });

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    restore: flow(function*(payload: any) {
      self.isManaging = true;

      try {
        return yield api.post(`/quick-link/restore`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),
  }))
  .create();

export default ContentResourcesQuickLinksStore.create();
